<template>
  <div class="block">
    <h2>{{ upsell ? $t(`upsell.choosePeriodCertificate`) : $t(`billingCycles.selectBillingCycle`) }} <v-progress-circular :size="20" width="2" color="primary" indeterminate v-if="paymentPeriods.length < 1" class="ml-3"/></h2>

    <p :class="['alert', !freeSslCondition || 'free-ssl']" v-if="freeDomainInfo.freeDomain && freeDomainInfo.freeDomainTlds.length > 0" data-attr="domain-gift">
      {{ $t(giftMessageKey, giftMessageParams) }}<span style="white-space: nowrap;">&#160;<v-tooltip right max-width="330">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
              color="success"
              icon
              v-bind="attrs"
              v-on="on"
              tag="span"
              fab
          >
            <v-icon class="sr-icon-gift"/>
          </v-btn>
        </template>
        <div v-html="freeDomainMessage"></div>
      </v-tooltip>
    </span>
    </p>

    <v-radio-group v-model="paymentPeriod" return-object class="payment-periods">
      <v-radio
          v-for="item in items"
          :key="item.period"
          :value="item"
          :data-attr="`billingcycle-${item.period}`"
      >
        <template v-slot:label>
          <div :class="['label-row', !isDiscount40(item) || 'monthly-discount40']">

            <div class="data">
              <div class="name">
                {{ $t('paymentPeriods.'+item.period) }}
                <span v-if="minCyclePrice(item.price, item.month) < monthly" class="highlight">&#8211;{{ discountPercent(item.price, item.month) }}%</span>
              </div>

              <div
                  class="hint"
                  v-if="item.freedomain || isPeriodForSsl(item.period)"
                  data-attr="billingcycle-gift"
              >
                <v-icon class="sr-icon-gift"/>
                <span>
                  <template v-if="item.freedomain">
                    <v-tooltip right max-width="330">
                      <template v-slot:activator="{ on }">
                        <span
                            class="link"
                            v-on="on"
                            @click.stop.prevent
                            v-html="$t('billingCycles.domain')"
                        ></span>
                      </template>
                      <div v-html="domainType === $options.DOMAIN_TYPE.KVM ? $t('registerFreeDomainKvm', {zones: freeDomainTldsList }) : $t('registerFreeDomain', {zones: freeDomainTldsList })"></div>
                    </v-tooltip>
                  </template>

                  <template v-if="item.freedomain && isPeriodForSsl(item.period)">, </template>

                  <template v-if="isPeriodForSsl(item.period)">
                    <v-tooltip right max-width="330">
                      <template v-slot:activator="{ on }">
                        <span
                            class="link"
                            v-on="on"
                            @click.stop.prevent
                            v-html="$t('billingCycles.ssl')"
                        ></span>
                      </template>
                      <div v-html="$t('billingCycles.domainSslTooltip')"></div>
                    </v-tooltip>
                  </template>

                  {{ $t('billingCycles.asGift') }}
                </span>
              </div>

              <div
                v-if="isDiscount40(item)"
                class="discount40"
              >
              {{ $t('billingCycles.promo') }}
              <v-tooltip bottom max-width="330">
                <template v-slot:activator="{ on }">
                  <span class="link" v-on="on" @click.stop="$event.preventDefault()">{{ $t('billingCycles.discount40') }}</span>
                </template>
                {{ $t('billingCycles.monthlyDiscount40Tooltip') }}
              </v-tooltip>
              {{ $t('billingCycles.monthlyDiscount40') }}
            </div>
            </div>

            <div class="details">
              <template v-if="isFreeTrialMonth(item)">
                <span class="benefit" v-html="$t('billingCycles.freeTestMonthly')" data-attr="free-test-monthly"></span>
              </template>

              <template v-else>
                <div
                    v-if="item.price !== monthly"
                    class="monthly"
                >
                  <span
                      v-if="(domainType === $options.DOMAIN_TYPE.SSL || domainType === $options.DOMAIN_TYPE.TEST_SSL) && item.month >= 13"
                      v-html="priceTransform(minCyclePrice(item.price, item.month , 12), currencyInfo.suffix, '/'+$tc('year', 1), currencyInfo.prefix)"
                  ></span>
                  <span
                      v-else-if="domainType !== $options.DOMAIN_TYPE.SSL && domainType !== $options.DOMAIN_TYPE.TEST_SSL"
                      v-html="priceTransform(minCyclePrice(item.price, item.month), currencyInfo.suffix, '/'+$t('month'), currencyInfo.prefix)"
                  ></span>
                </div>

                <div class="extra">
                  <span
                      v-if="minCyclePrice(item.price, item.month) < monthly && !item.charity"
                      :class="[item.info ? 'warn' : '', 'benefit']"
                  >
                    ({{ $t('billingCycles.economy') }} <span v-html="priceTransform(discountValue(item.price, item.month), currencyInfo.suffix, '', currencyInfo.prefix)"></span>)
                  </span>

                  <span
                      class="total"
                      v-html="priceTransform(item.price, currencyInfo.suffix, '', currencyInfo.prefix)" :data-attr="`${item.period}`"
                      :data-attr-value="`${currencyInfo.prefix || ''}${item.price}${currencyInfo.suffix || ''}`"
                  ></span>

                  <span
                      v-if="isDiscount40(item)"
                      class="total price-discount40"
                      v-html="priceTransform(getDiscount40(item.price), currencyInfo.suffix, '', currencyInfo.prefix)" :data-attr="`${item.period}`" :data-attr-value="`${currencyInfo.prefix || ''}${item.price}${currencyInfo.suffix || ''}`"
                  ></span>
                </div>
              </template>

              <template v-if="item.charity">
                <span class="benefit" v-html="$t('billingCycles.charity', {charity: `${currencyInfo.prefix || ''}${item.charity}${currencyInfo.suffix || ''}` })"></span>
              </template>
            </div>
          </div>
        </template>
      </v-radio>
    </v-radio-group>
  </div>
</template>

<script>
  import { priceTransform } from '@/modules/priceTransform';
  import { DOMAIN_TYPE } from '@/constants';

  export default {
    name: "PaymentPeriods",
    DOMAIN_TYPE,
    props: {
      paymentPeriods: { type: Array, default: () => ([]) },
      freeDomainInfo: { type: [Object, Array], default: () => ([]) },
      currencyInfo: { type: [Object, Array], default: () => ({}) },
      domainType: { type: String },
      freeTrial: { type: Boolean, default: false },
      routeQuery: { type: Object, default: () => ({}) },
      monthlyDiscount40: { type: Boolean, default: false },
      promo: { type: String, default: '' },
      fortyPercentPromo: { type: Array, default: () => ([]) },
      upsell: { type: Boolean, default: false },
      selectedPaymentPeriod: { type: Object, default: () => null },
      periodForFreeSsl: { type: String, default: '' },
    },
    data: () => ({
      paymentPeriod: {},
      items: [],
      monthly: 0,
    }),
    computed: {
      freeDomainTldsList() {
        let list = '';

        this.freeDomainInfo?.freeDomainTlds?.forEach(item => { list += `<span class="chip-sm v-chip v-size--default">${item}</span>` });
        return list;
      },
      freeSslCondition() {
        return this.periodForFreeSsl && this.freeDomainInfo.freeDomain && this.domainType !== DOMAIN_TYPE.KVM;
      },
      giftMessageKey() {
        return this.freeSslCondition
          ? 'billingCycles.domainGiftSslMessage'
          : 'billingCycles.domainGiftMessage';
      },
      giftMessageParams() {
        const period = this.$t(`periods.${this.freeDomainInfo.freedomainpaymentterms[0]}`);
        const more = this.freeDomainInfo.freedomainpaymentterms.length > 1
          ? this.$t('billingCycles.andMore')
          : '';

        let sslPeriods = '';
        if (this.periodForFreeSsl && this.periodForFreeSsl !== 'Any') {
          sslPeriods = this.$t('billingCycles.orderPeriods', {
            period: this.$t(`periods.${this.periodForFreeSsl.toLowerCase()}`)
          });
        } else if (this.periodForFreeSsl === 'Any' && this.$te('billingCycles.forAllPeriods')) {
          sslPeriods = this.$t('billingCycles.forAllPeriods');
        };

        return this.freeSslCondition
          ? { period, more, sslPeriods }
          : { period, more };
      },
      freeDomainMessage() {
        let key = 'registerFreeDomain';

        if (this.freeSslCondition) {
          key = 'registerFreeDomainSsl';
        } else if (this.domainType === this.$options.DOMAIN_TYPE.KVM) {
          key = 'registerFreeDomainKvm';
        }

        return this.$t(key, { zones: this.freeDomainTldsList });
      }
    },
    methods: {
      priceTransform,
      minCyclePrice(price, month, period) {
        return (Math.round((Number(price) / month * (period || 1)) * 100) / 100);
      },
      discountPercent(price, month) {
        return Math.round(100 - ((Number(price) / month) * 100 / this.monthly));
      },
      discountValue(price, month) {
        const defaultValue = (this.monthly * month);
        return Math.round((defaultValue - Number(price)) * 100) / 100;
      },
      isFreeTrialMonth(item) {
        return item ? this.freeTrial && item.period === 'monthly' : false;
      },
      getDiscount40(price) {
        return Math.round(Number(price) * 0.60 * 100) / 100;
      },
      isDiscount40(item) {
        return (this.monthlyDiscount40 && item.period === 'monthly');
      },
      setPaymentPeriod(period) {
        const currentIndex = period ? this.paymentPeriods.findIndex((item) => item.period === period.period) : 0;
        if (currentIndex !== -1) {
          this.paymentPeriod = this.paymentPeriods[currentIndex];
        }
      },
      isPeriodForSsl(period) {
        const sslPeriod = this.periodForFreeSsl?.toLowerCase();
        if (!sslPeriod) return false;
        if (sslPeriod === 'any') return true;

        const periods = this.items.map(item => item.period.toLowerCase());
        return periods.includes(period?.toLowerCase()) && periods.indexOf(period?.toLowerCase()) >= periods.indexOf(sslPeriod);
      }
    },
    watch: {
      paymentPeriod(obj) {
        if (!obj) return;

        const period = {
          key: 'payment-period',
          title: '',
          name: '',
          month: obj?.month,
          period: obj?.period,
          price: this.domainType === DOMAIN_TYPE.TEST_SSL ? '0.00' : obj?.price,
          renewal: obj?.price,
          currency: obj?.currency_code,
          freedomain: obj?.freedomain,
        };
        this.$emit('paymentPeriod', period);
      },
      selectedPaymentPeriod: {
        handler(newPeriod) {
          this.setPaymentPeriod(newPeriod || null);
        },
        immediate: true,
        deep: true,
      },
      paymentPeriods: {
        handler() {
          this.items = !this.paymentPeriods ? [] : this.paymentPeriods;

          if(this.paymentPeriods && this.paymentPeriods.length > 0) {
            let currentIndex = this.paymentPeriod && Object.keys(this.paymentPeriod).length > 0 ? this.paymentPeriods.findIndex(object => object.period === this.paymentPeriod.period) : 0;
            if(this.routeQuery.billingcycle) {
              const index = this.paymentPeriods.findIndex(object => object.period === this.routeQuery.billingcycle);
              if(index !== -1) { currentIndex = index }
            }
            this.paymentPeriod = this.paymentPeriods[currentIndex];
          }

          const firstPaymentPeriod = this.paymentPeriods[0];
          this.monthly = firstPaymentPeriod?.month ? Math.round(Number(firstPaymentPeriod.price / firstPaymentPeriod.month) * 100) / 100 : 0;
        },
        immediate: true,
        deep: true
      }
    }
  }
</script>

<style lang="scss">

  .payment-periods {
    margin-bottom: -9px;

    .v-radio {
      margin-bottom: 0 !important;
      align-items: flex-start;
      padding: 5px 0;
      position: relative;
      min-height: 66px;
      align-items: flex-start;

      &:not(:last-child) {
        border-bottom: 1px solid rgba($black, .3);
      }

      &:after {
        content: '';
        background: $blue-active;
        border-radius: 7px;
        height: calc(100% - 18px);
        width: calc(100% - 10px);
        position: absolute;
        top: 9px;
        right: 0;
        opacity: 0;
        visibility: hidden;
        transition: $transition;
        z-index: 1;
      }

      &:not(.v-item--active):hover {
        &:after {
          background: rgba($base-bg-color, .4);
          opacity: 1;
          visibility: visible;
        }
      }

      &.v-item--active {
        .v-icon__svg {
          display: none;
        }

        .v-input--selection-controls__input:before,
        .v-input--selection-controls__input:after,
        &:after {
          opacity: 1;
          visibility: visible;
        }
      }
    }

    .v-input--selection-controls__input {
      margin-top: 19px;
      z-index: 2;
    }

    .v-input--selection-controls__input {
      position: relative;

      &:before,
      &:after {
        content: '';
        position: absolute;
        opacity: 0;
        visibility: hidden;
      }

      &:before {
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background-color: $blue;
        top: 2px;
        left: 2px;
        box-shadow: 0px 0px 0px 3px $white;
      }

      &:after {
        width: 7px;
        height: 11px;
        border-bottom: 2.5px solid $white;
        border-right: 2.5px solid $white;
        transform: rotate(43deg);
        top: 5px;
        left: 8px;
      }
    }

    .v-icon__svg {
      background-color: $white;
      border-radius: 25px;
    }

    .discount40 {
      color: $green;
      font-weight: 700;
      font-size: 16px;

      .link {
        color: $green !important;

        &:after {
          background-color: transparent;
          border-top: 1px dashed $green;
        }
      }
    }
  }

  .alert {
    color: $green;
    margin-bottom: 11px !important;

    .v-btn {
      margin-top: -5px;
      margin-left: -4px;

      .v-icon {
        color: $green;
      }
    }

    &.free-ssl {
      display: flex;

      & > span {
        order: -1;
      }
    }
  }

  .label-row {
    display: flex;
    align-items: center;
    color: $black;
    width: 100%;
    padding: 20px 10px 21px 0;
    z-index: 2;

    @media (max-width: 850px) {
      flex-wrap: wrap;
    }

    &.monthly-discount40 {

      @media (min-width: $tablet) {

        .data {
          width: calc(100% - 150px);
        }

        .details {
          width: 150px;
        }
      }

      .total:not(.price-discount40) {
        color: #7F7F7F;
        text-decoration: line-through;
        font-size: 16px;
        padding-left: 0;

        @media (max-width: $tablet) {
          width: 100%;
          padding-bottom: 8px;
        }
      }
    }

    .data {
      width: 240px;
      padding-right: 6px;
      align-self: flex-start;

      @media (max-width: $tablet) {
        width: 100%;
        padding-right: 0;
        padding-bottom: 8px;
      }
    }

    .details {
      width: calc(100% - 240px);
      text-align: right;

      @media (max-width: $tablet) {
        width: 100%;
        text-align: left;
      }
    }

    .extra {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      align-items: center;
      width: 100%;

      @media (max-width: $tablet) {
        justify-content: flex-start;
      }
    }

    .name {
      font-size: 19px;
      font-weight: 700;
      padding-bottom: 4px;
    }

    .monthly,
    .benefit {
      font-weight: 700;
    }

    .total {
      padding-left: 11px;

      @media (max-width: $tablet) {
        padding-left: 0
      }
    }

    .monthly {
      padding-bottom: 4px;

      @media (max-width: $tablet) {
        padding-bottom: 8px;
      }
    }

    .benefit {
      font-size: 19px;
      color: $primary;

      @media (max-width: $tablet) {
        padding-bottom: 4px;
        width: 100%;
      }
    }

    .hint {
      font-size: 16px;
      color: $gray;
      display: flex;
      align-items: center;

      i {
        font-size: 17px;
        margin-right: 3px;
        color: $green;
      }

      .link {
        color: $gray !important;
        text-decoration: none;

        &:after {
          background-color: transparent;
          border-top: 1px dashed $gray;
        }
      }
    }

    .warn {
      color: $warning;
    }

    .v-btn--fab.v-size--default {
      color: $green;
      font-size: 16px;
    }
  }
</style>
